import React from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import io from "socket.io-client";
import Swal from "sweetalert2";
import {
  clearMemberSession,
  getMemberIdentity,
  getMemberToken,
  setMemberSession,
} from "member/memberSession";
import { getApiMessage, memberApi } from "member/memberApi";
import {
  memberTermsHighlights,
  memberTermsIntro,
  memberTermsSections,
} from "member/memberTerms";
import { baseUrl } from "stores";

const TENORS = {
  bulanan: [2, 4, 6, 8, 10],
  mingguan: [8, 16, 24, 32, 40],
};
const REGISTER_SIMPANAN_WAJIB = {
  bulanan: 60000,
  mingguan: 15000,
};
const NEWS = [
  "https://bap-storage.s3.ap-southeast-2.amazonaws.com/berita_pugar/berita-koperasi-1.png",
  "https://bap-storage.s3.ap-southeast-2.amazonaws.com/berita_pugar/berita-koperasi-2.png",
];

const DEFAULT_MEMBER_TERMS = {
  intro: memberTermsIntro,
  highlights: memberTermsHighlights,
  sections: memberTermsSections,
};

function useMemberTerms() {
  const [terms, setTerms] = React.useState(DEFAULT_MEMBER_TERMS);

  React.useEffect(() => {
    let active = true;

    memberApi
      .terms()
      .then((response) => {
        if (!active || !response.data.data) return;
        setTerms({
          intro: response.data.data.intro || memberTermsIntro,
          highlights: response.data.data.highlights || [],
          sections: response.data.data.sections || [],
        });
      })
      .catch(() => {
        // Konten bawaan tetap ditampilkan saat API belum tersedia.
      });

    return () => {
      active = false;
    };
  }, []);

  return terms;
}

function money(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function dateId(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(value));
}

function monthId(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    month: "long",
    year: "numeric",
  }).format(new Date(value));
}

function getJenisUser(value) {
  return value === "mingguan" ? "mingguan" : "bulanan";
}

function getRegisterSimpananWajib(jenisUser) {
  return REGISTER_SIMPANAN_WAJIB[getJenisUser(jenisUser)];
}

function getTenorUnit(jenisUser) {
  return getJenisUser(jenisUser) === "mingguan" ? "minggu" : "bulan";
}

function getTenorOptions(jenisUser) {
  return TENORS[getJenisUser(jenisUser)];
}

function getFullMonthDiff(startDate, endDate = new Date()) {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime())) return 0;

  let months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months -= 1;
  return Math.max(months, 0);
}

function getTotalSimpanan(simpanan) {
  return Number(simpanan?.totalSimpanan ?? simpanan?.total_simpanan ?? 0);
}

function getLoanStatus(item) {
  if (!item) return "-";
  return item.statusPengajuan === "Disetujui" && item.pencairan === true
    ? item.statusPembayaran
    : item.statusPengajuan;
}

function getLoanApplyBlockingMessage(loan, remainingInstallments) {
  if (!loan) return "";

  if (["Menunggu", "Diproses", "Revisi"].includes(loan.statusPengajuan)) {
    return `Masih ada pengajuan yang statusnya ${loan.statusPengajuan}.`;
  }

  if (
    loan.statusPengajuan === "Disetujui" &&
    loan.statusPembayaran === "Angsur" &&
    loan.pencairan === false
  ) {
    return "Pengajuan sebelumnya sudah disetujui tetapi belum dicairkan.";
  }

  if (
    loan.statusPengajuan === "Disetujui" &&
    loan.statusPembayaran === "Macet"
  ) {
    return "Masih ada pinjaman berstatus macet. Silahkan hubungi admin.";
  }

  if (
    loan.statusPengajuan === "Disetujui" &&
    loan.statusPembayaran === "Angsur" &&
    loan.pencairan === true
  ) {
    return `Masih ada ${remainingInstallments} angsuran berjalan. Selesaikan pelunasan terlebih dahulu sebelum mengajukan pinjaman baru.`;
  }

  return "";
}

function firstName(identity) {
  return (identity?.nama || "Anggota").split(" ").slice(0, 2).join(" ");
}

function notifyError(error, fallback) {
  Swal.fire("Gagal", getApiMessage(error, fallback), "error");
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (typeof value === "number" && value === 0) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function validateRequiredFields(fields) {
  const emptyField = fields.find((field) => isEmptyValue(field.value));

  if (emptyField) {
    Swal.fire(
      "Data belum lengkap",
      `${emptyField.label} wajib diisi.`,
      "warning",
    );
    return false;
  }

  return true;
}

function normalizePin(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 6);
}

function validatePin(value, label = "PIN") {
  if (!/^\d{6}$/.test(String(value || ""))) {
    Swal.fire(
      `${label} tidak valid`,
      `${label} harus angka dan tepat 6 digit.`,
      "warning",
    );
    return false;
  }

  return true;
}

async function confirmAction({
  title = "Lanjutkan proses?",
  text = "Pastikan data yang kamu masukkan sudah benar.",
  confirmButtonText = "Lanjutkan",
  icon = "question",
} = {}) {
  const result = await Swal.fire({
    title,
    text,
    icon,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: "Batal",
    reverseButtons: true,
    focusCancel: true,
    confirmButtonColor: "#6B2FE0",
    cancelButtonColor: "#64748b",
  });

  return result.isConfirmed;
}

function StatusBadge({ status }) {
  return <span className={`member-badge ${String(status || "").toLowerCase().replace(/\s/g, "-")}`}>{status || "-"}</span>;
}

function EmptyState({ title, body }) {
  return (
    <div className="member-empty">
      <div className="member-empty-icon">!</div>
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div className="member-section-header">
      <h2>{title}</h2>
      {action}
    </div>
  );
}

function BackButton({ to, label = "Kembali" }) {
  return (
    <Link className="member-back-button" to={to}>
      <i className="fas fa-arrow-left" />
      <span>{label}</span>
    </Link>
  );
}

function SecretInput({ value, onChange, className = "", ...props }) {
  const [visible, setVisible] = React.useState(false);

  return (
    <div className={`member-secret-input ${className}`}>
      <input
        {...props}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? "Sembunyikan teks" : "Tampilkan teks"}
        disabled={props.disabled}
      >
        <i className={visible ? "fas fa-eye" : "fas fa-eye-slash"} />
      </button>
    </div>
  );
}

function PinRuleHint() {
  return (
    <span className="member-hint">
      PIN wajib angka dan tepat 6 digit, tidak boleh kurang atau lebih.
    </span>
  );
}

function TermBody({ body, links = [] }) {
  return (
    <>
      {Array.isArray(body) ? (
        <ul className="member-terms-body-list">
          {body.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p>{body}</p>
      )}
      {links.length > 0 && (
        <div className="member-terms-links">
          {links.map((link) => (
            <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer">
              <i className="fas fa-external-link-alt" />
              <span>{link.label}</span>
            </a>
          ))}
        </div>
      )}
    </>
  );
}

function mergeChatMessage(messages, message) {
  if (!message?.idChat) return messages;
  if (messages.some((item) => item.idChat === message.idChat)) return messages;
  return [...messages, message];
}

function chatTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function isImageAttachment(attachment) {
  return String(attachment?.mimeType || "").startsWith("image/");
}

function attachmentLabel(attachment) {
  const size = Number(attachment?.size || 0);
  const sizeText =
    size >= 1024 * 1024
      ? `${(size / 1024 / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${attachment?.originalName || "Lampiran"} - ${sizeText}`;
}

function ChatAttachment({ attachment }) {
  if (!attachment?.url) return null;

  return (
    <a
      className={`chat-attachment ${isImageAttachment(attachment) ? "image" : ""}`}
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {isImageAttachment(attachment) ? (
        <img src={attachment.url} alt={attachment.originalName || "Lampiran"} />
      ) : (
        <i className="fas fa-file-alt" />
      )}
      <span>{attachmentLabel(attachment)}</span>
    </a>
  );
}

function validateChatAttachment(file) {
  if (!file) return true;
  if (file.size > 5 * 1024 * 1024) {
    Swal.fire("Lampiran terlalu besar", "Ukuran lampiran maksimal 5MB.", "warning");
    return false;
  }
  return true;
}

function useLoad(loader, deps = []) {
  const [state, setState] = React.useState({
    loading: true,
    data: null,
  });

  const load = React.useCallback(async () => {
    setState((current) => ({ ...current, loading: true }));
    try {
      const data = await loader();
      setState({ loading: false, data });
      return data;
    } catch (error) {
      setState({ loading: false, data: null });
      notifyError(error);
      return null;
    }
  }, deps);

  React.useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}

export function MemberLogin() {
  const history = useHistory();
  const [form, setForm] = React.useState({ email: "", password: "" });
  const [otp, setOtp] = React.useState("");
  const [requiresOtp, setRequiresOtp] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  async function submit(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Email", value: form.email },
      { label: "Password", value: form.password },
    ])) return;
    setLoading(true);
    try {
      const response = await memberApi.login(form);
      if (response.data.requiresOtp) {
        setRequiresOtp(true);
        Swal.fire("OTP dikirim", response.data.message, "info");
      } else {
        setMemberSession(response.data);
        history.replace("/member/home");
      }
    } catch (error) {
      notifyError(error, "Login gagal");
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Kode OTP", value: otp },
    ])) return;
    setLoading(true);
    try {
      const response = await memberApi.verifyBrowserOtp({
        ...form,
        otp,
      });
      setMemberSession(response.data);
      history.replace("/member/home");
    } catch (error) {
      notifyError(error, "Verifikasi OTP gagal");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="member-auth-card">
      <span className="member-kicker">PUGAR Web</span>
      <h1>Masuk anggota</h1>
      <p>Akses simpanan, pinjaman, notifikasi, dan profil seperti aplikasi mobile.</p>
      {!requiresOtp ? (
        <form onSubmit={submit} className="member-form">
          <label>Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <label>Password</label>
          <SecretInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete="current-password" />
          <button className="member-primary" disabled={loading}>{loading ? "Memproses..." : "Masuk"}</button>
        </form>
      ) : (
        <form onSubmit={verifyOtp} className="member-form">
          <div className="member-inline-note">
            Browser baru terdeteksi. Masukkan OTP yang dikirim ke email kamu.
          </div>
          <label>Kode OTP</label>
          <input value={otp} onChange={(e) => setOtp(e.target.value)} required />
          <button className="member-primary" disabled={loading}>{loading ? "Memverifikasi..." : "Verifikasi browser"}</button>
          <button type="button" className="member-secondary" onClick={() => setRequiresOtp(false)}>
            Ganti akun
          </button>
        </form>
      )}
      <div className="member-auth-links">
        <Link to="/member/auth/register">Daftar anggota</Link>
        <Link to="/member/auth/forgot-password">Lupa password</Link>
      </div>
    </div>
  );
}

export function MemberRegister() {
  const history = useHistory();
  const [step, setStep] = React.useState(1);
  const [user, setUser] = React.useState({
    nopeg: "",
    idUser: "",
    nama: "",
    jenisUser: "bulanan",
  });
  const [form, setForm] = React.useState({
    email: "",
    otp: "",
    password: "",
    pin: "",
    nominalSimpanan: REGISTER_SIMPANAN_WAJIB.bulanan,
  });
  const [loading, setLoading] = React.useState(false);

  async function cekNopeg(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Nomor pegawai", value: user.nopeg },
    ])) return;
    setLoading(true);
    try {
      const response = await memberApi.cekNopeg(user.nopeg);
      const jenisUser = getJenisUser(response.data.jenisUser);
      const nominalSimpanan =
        response.data.nominalSimpanan || getRegisterSimpananWajib(jenisUser);
      setUser((current) => ({
        ...current,
        idUser: response.data.idUser,
        nama: response.data.nama,
        jenisUser,
      }));
      setForm((current) => ({
        ...current,
        nominalSimpanan,
      }));
      setStep(2);
    } catch (error) {
      notifyError(error, "Nomor pegawai tidak valid");
    } finally {
      setLoading(false);
    }
  }

  async function sendOtp(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Email", value: form.email },
    ])) return;
    setLoading(true);
    try {
      const response = await memberApi.cekEmail({
        email: form.email,
        nama: user.nama,
        idUser: user.idUser,
      });
      Swal.fire("OTP dikirim", response.data.message, "success");
      setStep(3);
    } catch (error) {
      notifyError(error, "Gagal mengirim OTP");
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Kode OTP", value: form.otp },
    ])) return;
    setLoading(true);
    try {
      await memberApi.cekOtp({
        otp: form.otp,
        idUser: user.idUser,
        date: Date.now(),
      });
      setStep(4);
    } catch (error) {
      notifyError(error, "OTP tidak valid");
    } finally {
      setLoading(false);
    }
  }

  async function complete(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Password", value: form.password },
      { label: "PIN transaksi", value: form.pin },
      { label: "Simpanan wajib", value: form.nominalSimpanan },
    ])) return;
    if (!validatePin(form.pin, "PIN transaksi")) return;
    const confirmed = await confirmAction({
      title: "Kirim registrasi?",
      text: "Data registrasi akan dikirim untuk diverifikasi admin.",
      confirmButtonText: "Kirim registrasi",
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      const response = await memberApi.completeRegister({
        ...form,
        nopeg: user.nopeg,
        idUser: user.idUser,
      });
      Swal.fire("Registrasi terkirim", response.data.message || "Silahkan tunggu konfirmasi admin.", "success");
      history.replace("/member/auth/login");
    } catch (error) {
      notifyError(error, "Registrasi gagal");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="member-auth-card">
      <span className="member-kicker">Registrasi</span>
      <h1>Daftar anggota</h1>
      <div className="member-steps">
        {[1, 2, 3, 4].map((item) => <span key={item} className={item <= step ? "active" : ""}>{item}</span>)}
      </div>
      {step === 1 && (
        <form onSubmit={cekNopeg} className="member-form">
          <label>Nomor pegawai</label>
          <input value={user.nopeg} onChange={(e) => setUser({ ...user, nopeg: e.target.value })} required />
          <button className="member-primary" disabled={loading}>{loading ? "Mengecek..." : "Lanjut"}</button>
        </form>
      )}
      {step === 2 && (
        <form onSubmit={sendOtp} className="member-form">
          <div className="member-inline-note">Halo, <strong>{user.nama}</strong></div>
          <label>Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <button className="member-primary" disabled={loading}>{loading ? "Mengirim..." : "Kirim OTP"}</button>
        </form>
      )}
      {step === 3 && (
        <form onSubmit={verifyOtp} className="member-form">
          <label>Kode OTP</label>
          <input value={form.otp} onChange={(e) => setForm({ ...form, otp: e.target.value })} required />
          <button className="member-primary" disabled={loading}>{loading ? "Memeriksa..." : "Verifikasi"}</button>
        </form>
      )}
      {step === 4 && (
        <form onSubmit={complete} className="member-form">
          <label>Password</label>
          <SecretInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete="new-password" />
          <label>PIN transaksi</label>
          <SecretInput inputMode="numeric" pattern="[0-9]{6}" maxLength="6" value={form.pin} onChange={(e) => setForm({ ...form, pin: normalizePin(e.target.value) })} required autoComplete="off" />
          <PinRuleHint />
          <label>
            Simpanan wajib {getJenisUser(user.jenisUser) === "mingguan" ? "mingguan" : "bulanan"}
          </label>
          <input value={money(form.nominalSimpanan)} readOnly />
          <button className="member-primary" disabled={loading}>{loading ? "Menyimpan..." : "Kirim registrasi"}</button>
        </form>
      )}
      <div className="member-auth-links single">
        <Link to="/member/auth/login">Kembali ke login</Link>
      </div>
    </div>
  );
}

export function MemberForgotPassword() {
  const history = useHistory();
  const [step, setStep] = React.useState(1);
  const [data, setData] = React.useState({ email: "", otp: "", idUser: "", password: "" });
  const [loading, setLoading] = React.useState(false);

  async function requestOtp(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Email", value: data.email },
    ])) return;
    setLoading(true);
    try {
      const response = await memberApi.requestOtp(data.email);
      setData((current) => ({ ...current, idUser: response.data.idUser }));
      Swal.fire("OTP dikirim", response.data.message, "success");
      setStep(2);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Kode OTP", value: data.otp },
    ])) return;
    setLoading(true);
    try {
      await memberApi.cekOtp({ otp: data.otp, idUser: data.idUser, date: Date.now() });
      setStep(3);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  }

  async function savePassword(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Password baru", value: data.password },
    ])) return;
    const confirmed = await confirmAction({
      title: "Simpan password baru?",
      text: "Password lama akan diganti setelah proses ini berhasil.",
      confirmButtonText: "Simpan",
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      const response = await memberApi.resetPassword({ email: data.email, password: data.password });
      Swal.fire("Berhasil", response.data.message, "success");
      history.replace("/member/auth/login");
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="member-auth-card">
      <span className="member-kicker">Pemulihan</span>
      <h1>Lupa password</h1>
      {step === 1 && (
        <form className="member-form" onSubmit={requestOtp}>
          <label>Email</label>
          <input type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} required />
          <button className="member-primary" disabled={loading}>Kirim OTP</button>
        </form>
      )}
      {step === 2 && (
        <form className="member-form" onSubmit={verifyOtp}>
          <label>Kode OTP</label>
          <input value={data.otp} onChange={(e) => setData({ ...data, otp: e.target.value })} required />
          <button className="member-primary" disabled={loading}>Verifikasi</button>
        </form>
      )}
      {step === 3 && (
        <form className="member-form" onSubmit={savePassword}>
          <label>Password baru</label>
          <SecretInput value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} required autoComplete="new-password" />
          <button className="member-primary" disabled={loading}>Simpan password</button>
        </form>
      )}
      <div className="member-auth-links single">
        <Link to="/member/auth/login">Kembali ke login</Link>
      </div>
    </div>
  );
}

export function MemberHome() {
  const identity = getMemberIdentity();
  const terms = useMemberTerms();
  const [activeTab, setActiveTab] = React.useState("simpanan");
  const { data, loading, reload } = useLoad(async () => {
    const [profile, simpanan, pinjaman, notif, chat] = await Promise.all([
      memberApi.profile(),
      memberApi.simpanan(),
      memberApi.pinjaman(),
      memberApi.notifikasi(),
      memberApi.chatSummary(),
    ]);
    return {
      profile: profile.data.data?.[0],
      simpanan: simpanan.data.data?.[0],
      pinjaman: pinjaman.data.data || [],
      notif: notif.data.data?.response || [],
      chat: chat.data.data,
    };
  }, []);

  const ongoing = (data?.pinjaman || []).find((item) => ["Angsur", "Macet"].includes(getLoanStatus(item)));
  const recentSimpanan = data?.simpanan?.list_simpanan?.slice(0, 5) || [];
  const recentPinjaman = data?.pinjaman?.slice(0, 5) || [];

  return (
    <div className="member-page">
      <div className="member-hero">
        <div>
          <span className="member-kicker">Selamat datang</span>
          <h1>{firstName(identity)}</h1>
          <p>{data?.profile?.jabatan || "Anggota PUGAR"}</p>
        </div>
        <Link className="member-icon-button" to="/member/notifikasi">
          <i className="fas fa-bell" />
          {data?.notif?.filter((item) => item.statusNotif === false).length > 0 && <b />}
        </Link>
      </div>
      <div className="member-news-grid">
        {NEWS.map((src) => <img key={src} src={src} alt="Berita PUGAR" />)}
      </div>
      <div className="member-stat-grid">
        <div className="member-stat">
          <span>Total simpanan</span>
          <strong>{money(getTotalSimpanan(data?.simpanan))}</strong>
        </div>
        <div className="member-stat">
          <span>Simpanan wajib</span>
          <strong>{money(data?.simpanan?.nominalSimpanan || data?.profile?.nominalSimpanan)}</strong>
        </div>
        <div className="member-stat">
          <span>Limit pinjaman</span>
          <strong>{money(Math.min(data?.simpanan?.limitPengajuan || 0, 10000000))}</strong>
        </div>
        <div className="member-stat">
          <span>Pinjaman aktif</span>
          <strong>{ongoing ? money(ongoing.nominalSisaBayar) : "-"}</strong>
        </div>
        <Link className="member-stat member-stat-link" to="/member/chat">
          <span>Balasan admin</span>
          <strong>{data?.chat?.unreadForMember ? `${data.chat.unreadForMember} belum dibaca` : "Tidak ada"}</strong>
        </Link>
      </div>
      <div className="member-quick-actions">
        <Link to="/member/pinjaman/ajukan"><i className="fas fa-plus-circle" /> Ajukan pinjaman</Link>
        <Link to="/member/simpanan"><i className="fas fa-wallet" /> Simpanan</Link>
        <Link to="/member/pelunasan"><i className="fas fa-receipt" /> Pelunasan</Link>
        <Link to="/member/ketentuan"><i className="fas fa-info-circle" /> Ketentuan</Link>
        <Link to="/member/chat"><i className="fas fa-comments" /> Chat admin</Link>
        <Link to="/member/profile"><i className="fas fa-user" /> Profil</Link>
      </div>
      <section className="member-panel member-terms-preview">
        <SectionHeader title="Info ketentuan PUGAR" action={<Link className="member-secondary small" to="/member/ketentuan">Lihat semua</Link>} />
        <div className="member-terms-highlight-grid">
          {terms.highlights.map((item) => (
            <div className="member-terms-highlight" key={item.title}>
              <i className={item.icon} />
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </div>
          ))}
        </div>
      </section>
      <section className="member-panel">
        <div className="member-tabs">
          <button className={activeTab === "simpanan" ? "active" : ""} onClick={() => setActiveTab("simpanan")}>Simpanan</button>
          <button className={activeTab === "pinjaman" ? "active" : ""} onClick={() => setActiveTab("pinjaman")}>Pinjaman</button>
        </div>
        {loading ? <div className="member-loading">Loading...</div> : activeTab === "simpanan" ? (
          recentSimpanan.length ? recentSimpanan.map((item) => (
            <div className="member-list-row" key={item._id || item.idSimpanan}>
              <div><strong>{item.idSimpanan}</strong><span>{dateId(item.tglSimpanan)}</span></div>
              <strong className="member-green">{money(item.nominal)}</strong>
            </div>
          )) : <EmptyState title="Kosong" body="Belum ada simpanan." />
        ) : (
          recentPinjaman.length ? recentPinjaman.map((item) => (
            <Link className="member-list-row" key={item._id || item.idPengajuan} to={`/member/pinjaman/${item.idPengajuan}`}>
              <div><strong>{item.idPengajuan}</strong><span>{money(item.nominalPencairan || item.plafond)}</span></div>
              <StatusBadge status={getLoanStatus(item)} />
            </Link>
          )) : <EmptyState title="Kosong" body="Belum ada pinjaman." />
        )}
      </section>
    </div>
  );
}

export function MemberKetentuan() {
  const terms = useMemberTerms();

  return (
    <div className="member-page">
      <SectionHeader title="Ketentuan PUGAR" />
      <section className="member-terms-hero">
        <div>
          <span className="member-kicker">Panduan anggota</span>
          <h1>{terms.intro.title}</h1>
          <p>{terms.intro.body}</p>
        </div>
        <i className="fas fa-info-circle" />
      </section>
      <div className="member-terms-highlight-grid">
        {terms.highlights.map((item) => (
          <section className="member-terms-highlight card" key={item.title}>
            <i className={item.icon} />
            <strong>{item.title}</strong>
            <span>{item.body}</span>
          </section>
        ))}
      </div>
      <div className="member-terms-list">
        {terms.sections.map((section) => (
          <section className="member-panel member-terms-section" key={section.title}>
            <div className="member-terms-section-title">
              <i className={section.icon} />
              <h2>{section.title}</h2>
            </div>
            {section.items.map((item, index) => (
              <details className="member-terms-detail" key={item.title} open={index === 0}>
                <summary>{item.title}</summary>
                <TermBody body={item.body} links={item.links} />
              </details>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

export function MemberSimpanan() {
  const [year, setYear] = React.useState("semua");
  const [nominalBaru, setNominalBaru] = React.useState(50000);
  const { data, loading, reload } = useLoad(async () => {
    const historyRequest = memberApi
      .pengajuanSimpanan()
      .catch(() => ({ data: { data: [] } }));
    const [simpanan, history, profile] = await Promise.all([
      memberApi.simpanan(),
      historyRequest,
      memberApi.profile(),
    ]);
    return {
      simpanan: simpanan.data.data?.[0],
      history: history.data.data || [],
      profile: profile.data.data?.[0],
    };
  }, []);

  const list = (data?.simpanan?.list_simpanan || []).filter(Boolean);
  const years = Array.from(new Set(list.map((item) => new Date(item.tglSimpanan).getFullYear()))).sort((a, b) => b - a);
  const filtered = year === "semua" ? list : list.filter((item) => new Date(item.tglSimpanan).getFullYear() === Number(year));
  const total = filtered.reduce((sum, item) => sum + Number(item.nominal || 0), 0);
  const totalSimpanan = getTotalSimpanan(data?.simpanan);
  const jenisSimpanan = getJenisUser(data?.profile?.jenisUser || data?.simpanan?.jenisUser);
  const minimumSimpanan = getRegisterSimpananWajib(jenisSimpanan);
  const bulanJoin = getFullMonthDiff(data?.profile?.joinDate);
  const canChangeSimpanan = Boolean(data?.profile?.joinDate) && bulanJoin >= 10;

  React.useEffect(() => {
    if (data?.profile) {
      setNominalBaru(
        Number(data.profile.nominalSimpanan || minimumSimpanan),
      );
    }
  }, [data?.profile, minimumSimpanan]);

  async function submitUpgrade(event) {
    event.preventDefault();
    if (!canChangeSimpanan) {
      Swal.fire(
        "Belum bisa mengajukan",
        "Upgrade/downgrade simpanan dapat dilakukan setelah 10 bulan join koperasi.",
        "warning",
      );
      return;
    }
    if (!validateRequiredFields([
      { label: "Nominal baru", value: nominalBaru },
    ])) return;
    if (nominalBaru < minimumSimpanan || nominalBaru % 5000 !== 0) {
      Swal.fire(
        "Nominal tidak sesuai",
        `Nominal harus kelipatan 5.000 dan minimum ${money(minimumSimpanan)}.`,
        "warning",
      );
      return;
    }
    if (Number(nominalBaru) === Number(data?.profile?.nominalSimpanan || 0)) {
      Swal.fire("Nominal sama", "Nominal baru harus berbeda dari simpanan wajib saat ini.", "warning");
      return;
    }
    const confirmed = await confirmAction({
      title: "Ajukan perubahan simpanan?",
      text: `Simpanan wajib akan diajukan menjadi ${money(nominalBaru)}.`,
      confirmButtonText: "Ajukan",
    });
    if (!confirmed) return;
    try {
      await memberApi.upgradeSimpanan({
        nominalLama: data?.profile?.nominalSimpanan,
        nominalBaru,
      });
      Swal.fire("Berhasil", "Pengajuan perubahan simpanan berhasil dikirim.", "success");
      reload();
    } catch (error) {
      notifyError(error);
    }
  }

  return (
    <div className="member-page">
      <SectionHeader title="Simpanan" action={<button className="member-refresh" onClick={reload}>Refresh</button>} />
      <div className="member-stat-grid three">
        <div className="member-stat"><span>Total simpanan</span><strong>{money(totalSimpanan)}</strong></div>
        <div className="member-stat"><span>Total terfilter</span><strong>{money(total)}</strong></div>
        <div className="member-stat"><span>Simpanan wajib</span><strong>{money(data?.profile?.nominalSimpanan)}</strong></div>
      </div>
      <div className="member-grid-2">
        <section className="member-panel">
          <div className="member-toolbar">
            <select value={year} onChange={(e) => setYear(e.target.value)}>
              <option value="semua">Seluruh tahun</option>
              {years.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          {loading ? <div className="member-loading">Loading...</div> : filtered.length ? filtered.map((item) => (
            <div className="member-list-row" key={item._id || item.idSimpanan}>
              <div><strong>{item.idSimpanan}</strong><span>{dateId(item.tglSimpanan)}</span></div>
              <strong className="member-green">{money(item.nominal)}</strong>
            </div>
          )) : <EmptyState title="Kosong" body="Belum ada data simpanan." />}
        </section>
		        <section className="member-panel">
		          <h2>Upgrade/Downgrade simpanan</h2>
		          {!canChangeSimpanan ? (
		            <div className="member-warning">
		              Perubahan simpanan dapat diajukan setelah 10 bulan join koperasi.
		            </div>
		          ) : null}
		          <form className="member-form compact" onSubmit={submitUpgrade}>
	            <label>Nominal baru</label>
	            <input type="number" value={nominalBaru} step="5000" min={minimumSimpanan} onChange={(e) => setNominalBaru(Number(e.target.value))} required disabled={!canChangeSimpanan} />
	            <button className="member-primary" disabled={!canChangeSimpanan}>Ajukan perubahan</button>
	          </form>
	          <div className="member-history">
            {(data?.history || []).length ? data.history.map((item) => (
              <div className="member-list-row" key={item._id || item.idHistory}>
                <div><strong>{item.idHistory}</strong><span>{money(item.nominalLama)} ke {money(item.nominalBaru)}</span></div>
                <StatusBadge status={item.statusPengajuan} />
              </div>
            )) : <EmptyState title="Kosong" body="Belum ada pengajuan perubahan." />}
		          </div>
		        </section>
	      </div>
    </div>
  );
}

export function MemberPinjamanList() {
  const { data, loading, reload } = useLoad(async () => {
    const response = await memberApi.pinjaman();
    return response.data.data || [];
  }, []);

  return (
    <div className="member-page">
      <SectionHeader title="Pinjaman" action={<Link className="member-primary small" to="/member/pinjaman/ajukan">Ajukan</Link>} />
      <section className="member-panel">
        {loading ? <div className="member-loading">Loading...</div> : data?.length ? data.map((item) => (
          <Link className="member-list-row" key={item._id || item.idPengajuan} to={`/member/pinjaman/${item.idPengajuan}`}>
            <div>
              <strong>{item.idPengajuan}</strong>
              <span>{money(item.statusPembayaran ? item.nominalPencairan : item.plafond)}</span>
            </div>
            <StatusBadge status={getLoanStatus(item)} />
          </Link>
        )) : <EmptyState title="Kosong" body="Kamu belum ada pinjaman." />}
      </section>
      <button className="member-refresh" onClick={reload}>Refresh</button>
    </div>
  );
}

export function MemberLoanForm() {
  const history = useHistory();
  const [form, setForm] = React.useState({ plafond: 0, tenor: 2, pin: "" });
  const { data } = useLoad(async () => {
    const [simpanan, pinjaman] = await Promise.all([memberApi.simpanan(), memberApi.pinjaman()]);
    const info = simpanan.data.data?.[0] || {};
    const loans = pinjaman.data.data || [];
    const latestRelevantLoan = loans.find((item) => item.statusPengajuan !== "Ditolak");
    const remainingInstallments = latestRelevantLoan
      ? Number(latestRelevantLoan.tenor || 0) - Number(latestRelevantLoan.angsuranKe || 0)
      : 0;
    const blockingMessage = getLoanApplyBlockingMessage(
      latestRelevantLoan,
      remainingInstallments,
    );

	    return {
	      jenisUser: getJenisUser(info.jenisUser),
	      tenorUnit: info.tenorUnit || getTenorUnit(info.jenisUser),
	      tenorOptions: info.tenorOptions || getTenorOptions(info.jenisUser),
	      limit: Number(info.limitPengajuan || 0),
	      gajiPokok: info.gajiPokok || 0,
	      latestRelevantLoan,
	      remainingInstallments,
	      blockingMessage,
	    };
	  }, []);
	  React.useEffect(() => {
	    if (data?.tenorOptions?.length) {
	      setForm((current) => ({
	        ...current,
	        tenor: data.tenorOptions.includes(current.tenor)
	          ? current.tenor
	          : data.tenorOptions[0],
	      }));
	    }
	  }, [data?.tenorOptions]);
	  const showLimitAngsuran = false;
		  const limitAngsuran = Math.round((data?.gajiPokok || 0) / 3);
  const angsuran = Number(form.plafond || 0) / Number(form.tenor || 1);

  async function submit(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Nominal pinjaman", value: form.plafond },
      { label: "Tenor", value: form.tenor },
      { label: "PIN", value: form.pin },
    ])) return;
    if (!validatePin(form.pin)) return;
    if (Number(form.plafond) > Number(data?.limit || 0)) {
      Swal.fire("Melebihi limit", "Nominal pinjaman lebih dari limit.", "warning");
      return;
    }
    if (showLimitAngsuran && limitAngsuran && angsuran > limitAngsuran) {
      Swal.fire("Melebihi limit angsuran", `Limit angsuran kamu ${money(limitAngsuran)}.`, "warning");
      return;
    }
    if (data?.blockingMessage) {
      Swal.fire("Belum bisa mengajukan", data.blockingMessage, "warning");
      return;
    }
    const confirmed = await confirmAction({
      title: "Kirim pengajuan pinjaman?",
	      text: `Nominal ${money(form.plafond)} dengan tenor ${form.tenor} ${data?.tenorUnit || "bulan"} akan diajukan.`,
      confirmButtonText: "Kirim",
    });
    if (!confirmed) return;
    try {
      await memberApi.submitPinjaman(form);
      Swal.fire("Berhasil", "Pengajuan pinjaman berhasil dikirim.", "success");
      history.push("/member/pinjaman");
    } catch (error) {
      notifyError(error);
    }
  }

  return (
    <div className="member-page">
      <BackButton to="/member/pinjaman" label="Kembali ke pinjaman" />
      <SectionHeader title="Ajukan pinjaman" />
      <section className="member-panel">
        {data?.blockingMessage && (
          <div className="member-warning danger">{data.blockingMessage}</div>
        )}
        <form className="member-form" onSubmit={submit}>
          <label>Nominal pinjaman</label>
          <input type="number" value={form.plafond} min="1" onChange={(e) => setForm({ ...form, plafond: Number(e.target.value) })} required disabled={Boolean(data?.blockingMessage)} />
		          <span className="member-hint">
		            Limit pinjaman: {money(data?.limit)}
		            {showLimitAngsuran
		              ? `. Limit angsuran/${data?.tenorUnit || "bulan"}: ${money(limitAngsuran)}`
		              : ""}
		          </span>
          <label>Tenor</label>
          <select value={form.tenor} onChange={(e) => setForm({ ...form, tenor: Number(e.target.value) })} required disabled={Boolean(data?.blockingMessage)}>
	            {(data?.tenorOptions || TENORS.bulanan).map((item) => <option key={item} value={item}>{item} {data?.tenorUnit || "bulan"}</option>)}
          </select>
          <label>PIN</label>
          <SecretInput inputMode="numeric" pattern="[0-9]{6}" maxLength="6" value={form.pin} onChange={(e) => setForm({ ...form, pin: normalizePin(e.target.value) })} required disabled={Boolean(data?.blockingMessage)} autoComplete="off" />
          <button className="member-primary" disabled={Boolean(data?.blockingMessage)}>Kirim pengajuan</button>
        </form>
      </section>
    </div>
  );
}

export function MemberPinjamanDetail() {
  const { idPengajuan } = useParams();
  const history = useHistory();
  const [noteCancel, setNoteCancel] = React.useState("");
  const [revision, setRevision] = React.useState({ plafond: 0, tenor: 2, pin: "" });
  const { data, loading, reload } = useLoad(async () => {
    const pinjaman = await memberApi.pinjaman({ idPengajuan });
    const item = pinjaman.data.data?.[0];
    const angsuran = item?.pencairan ? await memberApi.angsuran(item.idPengajuan) : { data: { data: [] } };
    const simpanan = await memberApi.simpanan();
	    return {
	      item,
	      angsuran: angsuran.data.data || [],
	      limit: Number(simpanan.data.data?.[0]?.limitPengajuan || 0),
	      gajiPokok: simpanan.data.data?.[0]?.gajiPokok || 0,
	      jenisUser: getJenisUser(simpanan.data.data?.[0]?.jenisUser || item?.jenisUser),
	      tenorUnit:
	        item?.tenorUnit ||
	        simpanan.data.data?.[0]?.tenorUnit ||
	        getTenorUnit(simpanan.data.data?.[0]?.jenisUser || item?.jenisUser),
	      tenorOptions:
	        simpanan.data.data?.[0]?.tenorOptions ||
	        getTenorOptions(simpanan.data.data?.[0]?.jenisUser || item?.jenisUser),
	    };
	  }, [idPengajuan]);

  React.useEffect(() => {
    if (data?.item) {
      setRevision({
        plafond: data.item.plafond,
        tenor: data.item.tenor,
        pin: "",
      });
    }
  }, [data?.item]);

  async function cancel() {
    if (!validateRequiredFields([
      { label: "Alasan pembatalan", value: noteCancel },
    ])) return;
    const confirmed = await confirmAction({
      title: "Batalkan pengajuan?",
      text: "Permintaan pembatalan pengajuan akan dikirim ke admin.",
      confirmButtonText: "Batalkan pengajuan",
      icon: "warning",
    });
    if (!confirmed) return;
    try {
      await memberApi.cancelPinjaman({ idPengajuan, noteCancel });
      Swal.fire("Berhasil", "Pengajuan berhasil dibatalkan.", "success");
      history.push("/member/pinjaman");
    } catch (error) {
      notifyError(error);
    }
  }

  async function downloadForm() {
    try {
      const response = await memberApi.formPengajuan(idPengajuan);
      window.open(response.data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      notifyError(error);
    }
  }

  async function ajukanUlang(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Nominal pinjaman", value: revision.plafond },
      { label: "Tenor", value: revision.tenor },
      { label: "PIN", value: revision.pin },
    ])) return;
    if (!validatePin(revision.pin)) return;
    const confirmed = await confirmAction({
      title: "Kirim revisi pengajuan?",
	      text: `Revisi nominal ${money(revision.plafond)} dengan tenor ${revision.tenor} ${data?.tenorUnit || "bulan"} akan dikirim.`,
      confirmButtonText: "Kirim revisi",
    });
    if (!confirmed) return;
    try {
      await memberApi.ajukanUlang({
        ...revision,
        idPengajuan,
      });
      Swal.fire("Berhasil", "Revisi pengajuan berhasil dikirim.", "success");
      reload();
    } catch (error) {
      notifyError(error);
    }
  }

  const item = data?.item;
  const status = getLoanStatus(item);

  if (loading) return <div className="member-page"><div className="member-loading">Loading...</div></div>;
  if (!item) return <div className="member-page"><EmptyState title="Tidak ditemukan" body="Data pinjaman tidak tersedia." /></div>;

  return (
    <div className="member-page">
      <BackButton to="/member/pinjaman" label="Kembali ke pinjaman" />
      <SectionHeader title={idPengajuan} action={<button className="member-refresh" onClick={reload}>Refresh</button>} />
      {status === "Revisi" ? (
        <section className="member-panel">
          <div className="member-warning danger">Revisi karena: {item.noteReject || "-"}</div>
          <form className="member-form" onSubmit={ajukanUlang}>
            <label>Nominal pinjaman</label>
            <input type="number" value={revision.plafond} min="1" onChange={(e) => setRevision({ ...revision, plafond: Number(e.target.value) })} required />
            <label>Tenor</label>
            <select value={revision.tenor} onChange={(e) => setRevision({ ...revision, tenor: Number(e.target.value) })} required>
	              {(data?.tenorOptions || TENORS.bulanan).map((value) => <option key={value} value={value}>{value} {data?.tenorUnit || "bulan"}</option>)}
            </select>
            <label>PIN</label>
            <SecretInput inputMode="numeric" pattern="[0-9]{6}" maxLength="6" value={revision.pin} onChange={(e) => setRevision({ ...revision, pin: normalizePin(e.target.value) })} required autoComplete="off" />
            <button className="member-primary">Kirim revisi</button>
          </form>
        </section>
      ) : (
        <>
          <div className="member-loan-card">
            <div>
              <span>{["Menunggu", "Diproses"].includes(status) ? "Plafond diajukan" : "Sisa pembayaran"}</span>
              <strong>{money(["Menunggu", "Diproses"].includes(status) ? item.plafond : item.nominalSisaBayar)}</strong>
            </div>
            <StatusBadge status={status} />
          </div>
          <div className="member-stat-grid three">
	            <div className="member-stat"><span>Tenor</span><strong>{item.tenor} {data?.tenorUnit || item.tenorUnit || "bulan"}</strong></div>
	            <div className="member-stat"><span>Angsuran/{data?.tenorUnit || item.tenorUnit || "bulan"}</span><strong>{money((item.nominalDibayarkanPerPeriode || item.nominalDibayarkanPerBulan || 0) + (item.nominalAdministrasiPerPeriode || item.nominalAdministrasiPerBulan || 0))}</strong></div>
            <div className="member-stat"><span>Pencairan</span><strong>{money(item.nominalPencairan || item.plafond)}</strong></div>
          </div>
          <section className="member-panel">
            <SectionHeader title="Angsuran" action={<button className="member-refresh" onClick={downloadForm}>Form PDF</button>} />
            {data.angsuran.length ? data.angsuran.map((row) => (
              <div className="member-list-row" key={row._id || row.idAngsuran}>
                <div>
                  <strong>{monthId(row.dateTempo)}</strong>
                  <span>Angsuran {money((row.nominalAngsuran || 0) + (row.nominalAdministrasi || 0))}</span>
                  <span>Terbayar {money((row.nominalPembayaran || 0) + (row.nominalBayarAdministrasi || 0))}</span>
                </div>
                <StatusBadge status={status === "Lunas" ? "Lunas" : row.statusAngsuran} />
              </div>
            )) : <EmptyState title="Belum ada angsuran" body="Angsuran akan muncul setelah pencairan." />}
          </section>
          {["Menunggu", "Diproses"].includes(status) && (
            <section className="member-panel">
              <h2>Batalkan pengajuan</h2>
              <div className="member-form compact">
                <textarea placeholder="Alasan pembatalan" value={noteCancel} onChange={(e) => setNoteCancel(e.target.value)} required />
                <button className="member-secondary danger" onClick={cancel}>Batalkan</button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export function MemberPelunasan() {
  const [checked, setChecked] = React.useState([]);
  const [totalTransfer, setTotalTransfer] = React.useState(0);
  const [file, setFile] = React.useState(null);
	  const { data, loading, reload } = useLoad(async () => {
	    const [pinjaman, pelunasan, rekening, simpanan] = await Promise.all([
	      memberApi.pinjaman(),
	      memberApi.pelunasan(),
	      memberApi.rekening().catch(() => ({ data: { data: [] } })),
	      memberApi.simpanan(),
	    ]);
	    const ongoing = (pinjaman.data.data || []).find((item) => ["Angsur", "Macet"].includes(getLoanStatus(item)));
	    const angsuran = ongoing ? await memberApi.angsuran(ongoing.idPengajuan) : { data: { data: [] } };
	    const jenisUser = getJenisUser(simpanan.data.data?.[0]?.jenisUser || ongoing?.jenisUser);
	    const tenorUnit = ongoing?.tenorUnit || getTenorUnit(jenisUser);
	    const unpaidAngsuran = (angsuran.data.data || [])
	      .filter((item) => item.statusAngsuran === "Belum Terbayarkan")
	      .sort((a, b) => new Date(a.dateTempo) - new Date(b.dateTempo));
	
	    return {
	      ongoing,
	      angsuran: unpaidAngsuran,
	      pelunasan: pelunasan.data.data || [],
	      rekening: rekening.data.data?.[0],
	      jenisUser,
	      tenorUnit,
	      canPelunasan: true,
	    };
	  }, []);
  React.useEffect(() => {
    if (!data?.ongoing?.idPengajuan) {
      setChecked([]);
      return;
    }

    setChecked((data.angsuran || []).map((item) => item.idAngsuran));
  }, [data?.ongoing?.idPengajuan, data?.angsuran]);

  const selectedRows = (data?.angsuran || []).filter((item) => checked.includes(item.idAngsuran));
  const totalPokok = selectedRows.reduce((sum, item) => sum + Number(item.nominalAngsuran || 0), 0);
  const totalAdmin = selectedRows.reduce((sum, item) => sum + Number(item.nominalAdministrasi || 0), 0);
  const total = totalPokok + totalAdmin;
  const nominalTransferMismatch = Number(totalTransfer) > 0 && total > 0 && Number(totalTransfer) !== total;
  const allInstallmentsSelected = Boolean(
    data?.angsuran?.length && checked.length === data.angsuran.length,
  );
  const canSubmitPelunasan = Boolean(
    data?.ongoing?.idPengajuan &&
    allInstallmentsSelected &&
    file &&
    total > 0 &&
    Number(totalTransfer) === total,
  );

  async function submit(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Pinjaman aktif", value: data?.ongoing?.idPengajuan },
      { label: "Angsuran dipilih", value: checked },
      { label: "Bukti transfer", value: file },
      { label: "Nominal transfer", value: totalTransfer },
    ])) return;
    if (!allInstallmentsSelected) {
      Swal.fire(
        "Data belum lengkap",
        "Pelunasan wajib mencakup semua sisa angsuran.",
        "warning",
      );
      return;
    }
    if (Number(totalTransfer) !== total) {
      Swal.fire("Data belum sesuai", "Nominal transfer harus sama dengan total seluruh sisa angsuran.", "warning");
      return;
    }
    const confirmed = await confirmAction({
      title: "Kirim pengajuan pelunasan?",
      text: `Total seluruh sisa angsuran adalah ${money(total)}.`,
      confirmButtonText: "Kirim pelunasan",
    });
    if (!confirmed) return;
    const formData = new FormData();
    formData.append("buktiTfUser", file, file.name);
    formData.append("idPengajuan", data.ongoing.idPengajuan);
    formData.append("nominalPokokPelunasan", totalPokok);
    formData.append("nominalAdminPelunasan", totalAdmin);
    formData.append("idAngsuran", JSON.stringify(checked));
    formData.append("untilEnd", checked.length === data.angsuran.length);
    try {
      await memberApi.submitPelunasan(formData);
      Swal.fire("Berhasil", "Pengajuan pelunasan berhasil dikirim.", "success");
      setChecked([]);
      setFile(null);
      setTotalTransfer(0);
      reload();
    } catch (error) {
      notifyError(error);
    }
  }

  return (
    <div className="member-page">
      <SectionHeader title="Percepatan pelunasan" action={<button className="member-refresh" onClick={reload}>Refresh</button>} />
      <div className="member-grid-2">
        <section className="member-panel">
          <h2>Ajukan pelunasan</h2>
	          {loading ? <div className="member-loading">Loading...</div> : data?.ongoing && data?.canPelunasan ? (
            <form className="member-form compact" onSubmit={submit}>
              <div className="member-warning">Pinjaman aktif: {data.ongoing.idPengajuan}</div>
              {data?.rekening && (
                <div className="member-account-box">
                  <strong>{data.rekening.namaInstansi}</strong>
                  <span>{data.rekening.noRekening}</span>
                </div>
              )}
              <div className="member-check-list">
                {data.angsuran.map((item) => (
                  <label key={item.idAngsuran}>
                    <input
                      type="checkbox"
                      checked={checked.includes(item.idAngsuran)}
                      disabled
                      readOnly
                    />
	                    <span>{monthId(item.dateTempo)} - {money((item.nominalAngsuran || 0) + (item.nominalAdministrasi || 0))}</span>
	                  </label>
	                ))}
              </div>
              <label>Total seluruh sisa angsuran</label>
              <input value={money(total)} readOnly />
              <label>Nominal transfer</label>
              <input type="number" value={totalTransfer} min="1" onChange={(e) => setTotalTransfer(Number(e.target.value))} required />
              {nominalTransferMismatch && (
                <span className="member-hint danger">
                  Nominal transfer harus sama dengan total seluruh sisa angsuran: {money(total)}.
                </span>
              )}
              <label>Bukti transfer</label>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} required />
              <button className="member-primary" disabled={!canSubmitPelunasan}>
                Kirim pelunasan
              </button>
            </form>
	          ) : data?.ongoing && !data?.canPelunasan ? (
	            <EmptyState title="Belum tersedia" body="Pelunasan dipercepat belum tersedia untuk pinjaman ini." />
	          ) : <EmptyState title="Belum ada pinjaman aktif" body="Pelunasan tersedia saat ada pinjaman berjalan." />}
        </section>
        <section className="member-panel">
          <h2>Riwayat pelunasan</h2>
          {(data?.pelunasan || []).length ? data.pelunasan.map((item) => (
            <div className="member-list-row" key={item._id || item.idPelunasan}>
              <div><strong>{item.idPelunasan || monthId(item.tglPengajuan)}</strong><span>{money(item.nominalTotal)}</span></div>
              <StatusBadge status={item.statusPengajuan} />
            </div>
          )) : <EmptyState title="Kosong" body="Belum ada pengajuan pelunasan." />}
        </section>
      </div>
    </div>
  );
}

export function MemberNotifikasi() {
  const { data, loading, reload } = useLoad(async () => {
    const response = await memberApi.notifikasi();
    return response.data.data?.response || [];
  }, []);

  async function read(item) {
    try {
      await memberApi.readNotifikasi(item.idNotif);
      Swal.fire(item.title, item.message, "info");
      reload();
    } catch (error) {
      notifyError(error);
    }
  }

  async function remove(item) {
    const confirmed = await confirmAction({
      title: "Hapus notifikasi?",
      text: "Notifikasi ini akan dihapus dari daftar kamu.",
      confirmButtonText: "Hapus",
      icon: "warning",
    });
    if (!confirmed) return;
    try {
      await memberApi.deleteNotifikasi(item.idNotif);
      reload();
    } catch (error) {
      notifyError(error);
    }
  }

  async function removeAll() {
    const confirmed = await confirmAction({
      title: "Hapus semua notifikasi?",
      text: "Semua notifikasi akan dihapus dari daftar kamu.",
      confirmButtonText: "Hapus semua",
      icon: "warning",
    });
    if (!confirmed) return;
    try {
      await memberApi.deleteAllNotifikasi();
      reload();
    } catch (error) {
      notifyError(error);
    }
  }

  return (
    <div className="member-page">
      <SectionHeader title="Notifikasi" action={<button className="member-refresh" onClick={removeAll}>Hapus semua</button>} />
      <section className="member-panel">
        {loading ? <div className="member-loading">Loading...</div> : data?.length ? data.map((item) => (
          <div className={`member-notif ${item.statusNotif === false ? "unread" : ""}`} key={item._id || item.idNotif}>
            <button onClick={() => read(item)}>
              <strong>{item.title}</strong>
              <span>{dateId(item.dateNotif)}</span>
              <p>{item.message}</p>
            </button>
            <button className="member-trash" onClick={() => remove(item)}><i className="fas fa-trash" /></button>
          </div>
        )) : <EmptyState title="Kosong" body="Belum ada notifikasi." />}
      </section>
    </div>
  );
}

export function MemberProfile() {
  const identity = getMemberIdentity();
  const [passwordForm, setPasswordForm] = React.useState({ passwordLama: "", passwordBaru: "" });
  const [pinForm, setPinForm] = React.useState({ password: "", otp: "", pin: "", otpSent: false });
  const [emailBaru, setEmailBaru] = React.useState("");
  const { data, loading, reload } = useLoad(async () => {
    const response = await memberApi.profile();
    return response.data.data?.[0];
  }, []);

  async function logout(confirm = true) {
    if (confirm) {
      const confirmed = await confirmAction({
        title: "Keluar dari akun?",
        text: "Kamu perlu login ulang untuk mengakses portal member.",
        confirmButtonText: "Keluar",
        icon: "warning",
      });
      if (!confirmed) return;
    }
    clearMemberSession();
    window.location.replace("/member/auth/login");
  }

  async function formRegister() {
    try {
      const response = await memberApi.formRegister();
      window.open(response.data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      notifyError(error);
    }
  }

  async function changePassword(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Password lama", value: passwordForm.passwordLama },
      { label: "Password baru", value: passwordForm.passwordBaru },
    ])) return;
    const confirmed = await confirmAction({
      title: "Ubah password?",
      text: "Setelah berhasil, kamu akan diminta login ulang.",
      confirmButtonText: "Ubah password",
    });
    if (!confirmed) return;
    try {
      await memberApi.ubahPassword(passwordForm);
      Swal.fire("Berhasil", "Password berhasil diubah. Silahkan login ulang.", "success");
      logout(false);
    } catch (error) {
      notifyError(error);
    }
  }

  async function sendOtpPin(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Password akun", value: pinForm.password },
    ])) return;
    try {
      await memberApi.cekPasswordLupaPin({
        email: data?.email,
        password: pinForm.password,
      });
      Swal.fire("OTP dikirim", "Silahkan cek email kamu.", "success");
      setPinForm({ ...pinForm, otpSent: true });
    } catch (error) {
      notifyError(error);
    }
  }

  async function changePin(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "OTP", value: pinForm.otp },
      { label: "PIN baru", value: pinForm.pin },
    ])) return;
    if (!validatePin(pinForm.pin, "PIN baru")) return;
    const confirmed = await confirmAction({
      title: "Simpan PIN baru?",
      text: "PIN transaksi kamu akan diganti.",
      confirmButtonText: "Simpan PIN",
    });
    if (!confirmed) return;
    try {
      await memberApi.cekOtp({
        otp: pinForm.otp,
        idUser: data?.idUser || identity?.idUser,
        date: Date.now(),
      });
      await memberApi.ubahPin(pinForm.pin);
      Swal.fire("Berhasil", "PIN berhasil diubah.", "success");
      setPinForm({ password: "", otp: "", pin: "", otpSent: false });
    } catch (error) {
      notifyError(error);
    }
  }

  async function changeEmail(event) {
    event.preventDefault();
    if (!validateRequiredFields([
      { label: "Email baru", value: emailBaru },
    ])) return;
    const confirmed = await confirmAction({
      title: "Ubah email?",
      text: "Setelah berhasil, kamu akan diminta login ulang.",
      confirmButtonText: "Ubah email",
    });
    if (!confirmed) return;
    try {
      await memberApi.ubahEmail(emailBaru);
      Swal.fire("Berhasil", "Email berhasil diubah. Silahkan login ulang.", "success");
      logout(false);
    } catch (error) {
      notifyError(error);
    }
  }

  return (
    <div className="member-page">
      <SectionHeader title="Profil" action={<button className="member-secondary small" onClick={logout}>Keluar</button>} />
      <section className="member-profile-card">
        <img src={require("assets/img/user.png").default} alt="Profil" />
        <div>
          <h1>{firstName(identity)}</h1>
          <p>{loading ? "Loading..." : data?.jabatan || "-"}</p>
        </div>
      </section>
      <div className="member-stat-grid three">
        <div className="member-stat"><span>Nopeg</span><strong>{data?.nopeg || identity?.nopeg || "-"}</strong></div>
        <div className="member-stat"><span>Email</span><strong>{data?.email || "-"}</strong></div>
        <div className="member-stat"><span>No. telepon</span><strong>{data?.noTelp || "-"}</strong></div>
      </div>
      <div className="member-grid-2">
        <section className="member-panel">
          <h2>Dokumen & email</h2>
          <button className="member-primary full" onClick={formRegister}>Lihat form registrasi</button>
          <form className="member-form compact" onSubmit={changeEmail}>
            <label>Email baru</label>
            <input type="email" value={emailBaru} onChange={(e) => setEmailBaru(e.target.value)} required />
            <button className="member-secondary">Ubah email</button>
          </form>
        </section>
        <section className="member-panel">
          <h2>Ubah password</h2>
          <form className="member-form compact" onSubmit={changePassword}>
            <label>Password lama</label>
            <SecretInput value={passwordForm.passwordLama} onChange={(e) => setPasswordForm({ ...passwordForm, passwordLama: e.target.value })} required autoComplete="current-password" />
            <label>Password baru</label>
            <SecretInput value={passwordForm.passwordBaru} onChange={(e) => setPasswordForm({ ...passwordForm, passwordBaru: e.target.value })} required autoComplete="new-password" />
            <button className="member-secondary">Ubah password</button>
          </form>
        </section>
        <section className="member-panel">
          <h2>Ubah PIN</h2>
          {!pinForm.otpSent ? (
            <form className="member-form compact" onSubmit={sendOtpPin}>
              <label>Password akun</label>
              <SecretInput value={pinForm.password} onChange={(e) => setPinForm({ ...pinForm, password: e.target.value })} required autoComplete="current-password" />
              <button className="member-secondary">Kirim OTP</button>
            </form>
          ) : (
            <form className="member-form compact" onSubmit={changePin}>
              <label>OTP</label>
              <input value={pinForm.otp} onChange={(e) => setPinForm({ ...pinForm, otp: e.target.value })} required />
              <label>PIN baru</label>
              <SecretInput inputMode="numeric" pattern="[0-9]{6}" maxLength="6" value={pinForm.pin} onChange={(e) => setPinForm({ ...pinForm, pin: normalizePin(e.target.value) })} required autoComplete="off" />
              <PinRuleHint />
              <button className="member-secondary">Simpan PIN</button>
            </form>
          )}
        </section>
      </div>
      <button className="member-refresh" onClick={reload}>Refresh profil</button>
    </div>
  );
}

export function MemberChat() {
  const identity = getMemberIdentity();
  const [messages, setMessages] = React.useState([]);
  const [message, setMessage] = React.useState("");
  const [attachment, setAttachment] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const listRef = React.useRef(null);
  const fileRef = React.useRef(null);

  const loadMessages = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await memberApi.chatMessages();
      setMessages(response.data.data || []);
    } catch (error) {
      notifyError(error, "Gagal memuat chat");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  React.useEffect(() => {
    if (!identity?.idUser) return undefined;

    const socket = io(baseUrl || window.location.origin);
    socket.emit("joinRoom", identity.idUser);
    socket.on("chat:message", (payload) => {
      if (payload?.message?.idUser === identity.idUser) {
        setMessages((current) => mergeChatMessage(current, payload.message));
      }
    });
    socket.on("chat:deleted", (payload) => {
      if (payload?.idUser === identity.idUser) {
        setMessages([]);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [identity?.idUser]);

  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  async function submit(event) {
    event.preventDefault();
    if (!message.trim() && !attachment) {
      Swal.fire("Data belum lengkap", "Pesan atau lampiran wajib diisi.", "warning");
      return;
    }

    setSending(true);
    try {
      const response = await memberApi.sendChatMessage(message, attachment);
      setMessages((current) => mergeChatMessage(current, response.data.data));
      setMessage("");
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (error) {
      notifyError(error, "Pesan gagal dikirim");
    } finally {
      setSending(false);
    }
  }

  function onAttachmentChange(event) {
    const file = event.target.files?.[0];
    if (!validateChatAttachment(file)) {
      event.target.value = "";
      setAttachment(null);
      return;
    }
    setAttachment(file || null);
  }

  return (
    <div className="member-page">
      <SectionHeader title="Chat Admin" action={<button className="member-refresh" onClick={loadMessages}>Refresh</button>} />
      <section className="member-chat-panel">
        <div className="member-chat-header">
          <div>
            <strong>Bantuan PUGAR</strong>
            <span>Pesan akan diterima admin secara realtime.</span>
          </div>
          <i className="fas fa-headset" />
        </div>
        <div className="member-chat-list" ref={listRef}>
          {loading ? (
            <div className="member-loading">Loading...</div>
          ) : messages.length ? (
            messages.map((item) => (
              <div
                className={`member-chat-bubble ${item.senderType === "member" ? "mine" : "theirs"}`}
                key={item.idChat}
              >
                <div>
                  <p>{item.message}</p>
                  {(item.attachments || []).map((file) => (
                    <ChatAttachment attachment={file} key={file.url || file.fileName} />
                  ))}
                  <span>{item.senderName || "-"} - {chatTime(item.dateChat)}</span>
                </div>
              </div>
            ))
          ) : (
            <EmptyState title="Belum ada chat" body="Kirim pesan pertama ke admin PUGAR." />
          )}
        </div>
        <form className="member-chat-form" onSubmit={submit}>
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tulis pesan..."
            maxLength="1000"
            disabled={sending}
          />
          <label className="member-chat-attach">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
              onChange={onAttachmentChange}
              disabled={sending}
            />
            <i className="fas fa-paperclip" />
          </label>
          <button className="member-primary" disabled={sending}>
            <i className="fas fa-paper-plane" />
            <span>{sending ? "Mengirim..." : "Kirim"}</span>
          </button>
        </form>
        {attachment && (
          <div className="member-chat-file-preview">
            <span>{attachment.name}</span>
            <button
              type="button"
              onClick={() => {
                setAttachment(null);
                if (fileRef.current) fileRef.current.value = "";
              }}
            >
              Hapus
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export function RequireMemberTokenNotice() {
  if (getMemberToken()) return null;
  return <div className="member-warning">Sesi anggota tidak ditemukan.</div>;
}
